import React, { FC, useMemo, memo } from 'react';
import { useSelector } from 'react-redux';

import { Campaign as CampaignType } from '@shared/types/mylounge';
import { getCategoryTabId } from '@client/redux/modules/mylounge';

import Campaign from '../campaign';
import { getColumnCount, mergeCampaignsWithGrid, getGrid } from './helpers';
import { useGridTrack, useGridHelpers } from './hooks';
import { GridBlockDimensions } from './constants';

import { Container, Wrapper } from './styles';

interface Props {
    width: number;
    campaigns: CampaignType[];
    isPlusEnabled: boolean;
}

export const Grid: FC<Props> = ({ width, campaigns, isPlusEnabled }) => {
    const categoryTabId = useSelector(getCategoryTabId);
    const columnCount = useMemo(() => getColumnCount(width), [width]);
    useGridTrack(columnCount);

    const grid = useMemo(() => {
        return getGrid(categoryTabId, columnCount, campaigns.length);
    }, [categoryTabId, columnCount, campaigns.length]);

    const campaignsWithGrid = useMemo(
        () => mergeCampaignsWithGrid(campaigns, grid),
        [campaigns, grid],
    );

    const { getCampaignStyles, gridHeight } = useGridHelpers(columnCount, width, campaignsWithGrid);

    let campaignIndex = 0; // only for upcoming campaings, banner position is not counted

    if (!campaigns) {
        return null;
    }

    return (
        <Container>
            <Wrapper $gridHeight={gridHeight} id="campaigns-open">
                {campaignsWithGrid.map(gridBlock => {
                    const elementStyle = getCampaignStyles(gridBlock);

                    return (
                        <Campaign
                            key={(gridBlock as CampaignType).campaignId}
                            campaign={gridBlock as CampaignType}
                            dimension={`${gridBlock.w}x${gridBlock.h}` as GridBlockDimensions}
                            campaignStyles={elementStyle}
                            isPlusEnabled={isPlusEnabled}
                            position={`${++campaignIndex}|${columnCount}`}
                        />
                    );
                })}
            </Wrapper>
        </Container>
    );
};

export default memo(Grid);
